import { useState, useContext } from "react";
import MainLayouts from "../../../Layout/MainLayouts";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import { ReactComponent as CardList } from "bootstrap-icons/icons/card-list.svg";
import Spinner from "../../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import CustomStyles from "../../../components/utils/CustomStyles";
import { handleError } from "../../../components/utils/AuthenticationCheck";
import { AuthContext } from "../../../AuthProvider/AuthProvider";
import formatDate from "../../../components/utils/DateFormatter";
import { useBranchInventoryRequests } from "../../../hooks/useAppData";

const getStatusClass = (status) => {
    switch (status) {
        case "pending": return "text-orange1";
        case "approved": return "text-green-600";
        case "partially_approved": return "text-blue-500";
        case "rejected": return "text-red-600";
        default: return "text-gray-600";
    }
};

const columns = [
    {
        name: "Request No",
        selector: (row) => row.request_number || "N/A",
        sortable: true,
        width: "200px",
    },
    {
        name: "Branch",
        selector: (row) => row.branch_name?.toUpperCase() || "N/A",
        sortable: true,
        width: "200px",
    },
    {
        name: "Head Office",
        selector: (row) => row.head_office_name || "N/A",
        sortable: true,
        width: "200px",
    },
    {
        name: "Total Items",
        selector: (row) => row.total_items || 0,
        sortable: true,
        width: "120px",
    },
    {
        name: "Qty Requested",
        selector: (row) => row.total_quantity_requested || 0,
        sortable: true,
        width: "150px",
    },
    {
        name: "Status",
        selector: (row) => row.status || "N/A",
        sortable: true,
        width: "160px",
        cell: (row) => (
            <span className={getStatusClass(row.status)}>
                {row.status?.toUpperCase()}
            </span>
        ),
    },
    {
        name: "Requested At",
        selector: (row) => formatDate(row.requested_at) || "N/A",
        width: "200px",
    },
];

// Defined outside component so the reference never changes
const ExpandableComponent = ({ data }) => (
    <div className="flex justify-start p-4 relative">
        <div className="mx-2 my-2 group relative">
            <Link
                to="/operations/branch/inventory-requests/details"
                state={{ requestId: data.id }}
            >
                <Eye className="w-5 h-5 text-dash4" />
            </Link>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 hidden group-hover:block text-darkGreen3 text-xs px-2 py-1 rounded">
                Details / Approve
            </div>
        </div>
    </div>
);

const BranchInventoryRequests = () => {
    const [filterText, setFilterText] = useState("");
    const { permissions } = useContext(AuthContext);
    const navigate = useNavigate();

    const { data: requestsResponse, isLoading: loading } = useBranchInventoryRequests();
    const requests = requestsResponse?.data?.requests || requestsResponse?.data || [];

    const handleFilter = (e) => {
        setFilterText(e.target.value);
    };

    const filteredItems = requests.filter((item) =>
        item.request_number?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.branch_name?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.head_office_name?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.status?.toLowerCase().includes(filterText.toLowerCase())
    );

    const canView = permissions.some(p => p.name === 'admin.view');

    return (
        <MainLayouts>
            <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
                <section className="w-full pr-[3%] pt-3 my-3" style={{ backgroundColor: "#F7F7F7" }}>
                    <nav className="bg-gray-200 py-0 px-4 rounded-lg">
                        <ol className="list-none p-0 inline-flex mb-2">
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">Operations</p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">Branch</p>
                                <ChevronRightIcon />
                            </li>
                            <li className="flex items-center">
                                <p className="text-orange1">Inventory Requests (Pending)</p>
                            </li>
                        </ol>
                    </nav>
                </section>

                <div className="flex justify-between items-center mb-4 mt-4">
                    <Link
                        to="/operations/branch/inventory-requests/history"
                        className="inline-flex items-center gap-2 bg-darkGreen3 text-white px-4 py-2 rounded hover:bg-green-800 transition text-sm"
                    >
                        <CardList className="w-4 h-4" />
                        View History
                    </Link>
                </div>

                <div className="pb-4 bg-white dark:bg-gray-900">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative flex-grow min-w-[200px]">
                            <input
                                type="search"
                                className="block w-full pl-4 pr-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50"
                                placeholder="Search requests..."
                                value={filterText}
                                onChange={handleFilter}
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <Spinner loading={loading} />
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredItems}
                        expandableRows={canView}
                        expandableRowsComponent={ExpandableComponent}
                        pagination
                        customStyles={CustomStyles}
                        noDataComponent={<div className="p-4 text-center text-gray-500">No pending inventory requests found</div>}
                    />
                )}
            </div>
        </MainLayouts>
    );
};

export default BranchInventoryRequests;
